/**
 * @fileoverview Filtre Islemleri Hook'u
 *
 * Bu modul, arama filtrelerinin uygulanmasi ve kaldirilmasini yoneten
 * custom hook'u icerir. Filtre string'lerini parse etme, tek bir filtreyi
 * kaldirma ve yeni filtre degerleri uygulama islevlerini saglar.
 *
 * @module useFilterHandlers
 */

import { useCallback, useMemo } from "react";
import { parseFilterString, removeFilterKey } from "../utils";

/**
 * @typedef {Object} UseFilterHandlersOptions
 * @property {Object} query - Mevcut arama sorgusu
 * @property {string} [query.path] - Arama yolu
 * @property {string} [query.filters] - Filtre string'i
 * @property {string} pathDraft - Kullanilacak arama yolu (draft)
 * @property {Function} callSearch - Arama fonksiyonu
 */

/**
 * @typedef {Object.<string, string[]>} FilterMap
 * Filtre anahtari -> deger dizisi eslesmesi
 * Ornek: { "oda-sayisi": ["2-1", "3-1"], "fiyat-max": ["5000000"] }
 */

/**
 * @typedef {Object} UseFilterHandlersReturn
 * @property {FilterMap} queryFilters - Parse edilmis mevcut filtreler
 * @property {Function} handleRemoveFilter - Filtre kaldirma fonksiyonu
 * @property {Function} handleApplyFilter - Filtre uygulama fonksiyonu
 */

/**
 * Filtre islemlerini yonetir.
 *
 * Bu hook, arama filtrelerinin eklenmesi, degistirilmesi ve kaldirilmasini
 * kolaylastirir. Filtre string'lerini objeye donusturur ve tersine.
 *
 * **Filtre String Formati:**
 * `key1=value1,value2&key2=value3`
 * Ornek: `oda-sayisi=2-1,3-1&fiyat-max=5000000`
 *
 * **Islemler:**
 * - `handleRemoveFilter(key)`: Belirli bir filtreyi tamamen kaldirir
 * - `handleApplyFilter(key, values)`: Belirli bir filtreyi gunceller
 *   - values bossa, filtre kaldirilir
 *   - values doluysa, filtre degerleri guncellenir
 *
 * @function useFilterHandlers
 * @param {UseFilterHandlersOptions} options - Hook opsiyonlari
 * @returns {UseFilterHandlersReturn} Filtre islem fonksiyonlari
 *
 * @example
 * const {
 *   queryFilters,
 *   handleRemoveFilter,
 *   handleApplyFilter,
 * } = useFilterHandlers({
 *   query: output?.query,
 *   pathDraft: currentPath,
 *   callSearch,
 * });
 *
 * // Filtre rozeti tiklandiginda kaldir
 * <FilterBadge
 *   label="3+1"
 *   onRemove={() => handleRemoveFilter("oda-sayisi")}
 * />
 *
 * // Filtre secimi degistiginde uygula
 * <FilterSelect
 *   value={queryFilters["oda-sayisi"]}
 *   onChange={(values) => handleApplyFilter("oda-sayisi", values)}
 * />
 */
export function useFilterHandlers({ query, pathDraft, callSearch }) {
  const queryFilters = useMemo(
    () => parseFilterString(query?.filters ?? ""),
    [query?.filters]
  );

  const handleRemoveFilter = useCallback(
    (key) => {
      const newFilters = removeFilterKey(query?.filters ?? "", key);
      callSearch({ path: pathDraft, filters: newFilters });
    },
    [query?.filters, pathDraft, callSearch]
  );

  const handleApplyFilter = useCallback(
    (key, values) => {
      const currentFilters = { ...queryFilters };
      if (values.length > 0) {
        currentFilters[key] = values;
      } else {
        delete currentFilters[key];
      }
      const newFiltersStr = Object.entries(currentFilters)
        .map(([k, v]) => `${k}=${v.join(",")}`)
        .join("&");
      callSearch({ path: pathDraft, filters: newFiltersStr });
    },
    [queryFilters, pathDraft, callSearch]
  );

  return {
    queryFilters,
    handleRemoveFilter,
    handleApplyFilter,
  };
}

export default useFilterHandlers;
